import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface ExchangeRate {
  id: number
  date: string
  currency_from: string
  currency_to: string
  buy_rate: string
  sell_rate: string
}

interface Props {
  [key: string]: unknown
  exchange_rate: ExchangeRate
}

export default function ExchangeRateEdit() {
  const { exchange_rate: rate } = usePage<Props>().props

  const { data, setData, put, processing, errors } = useForm({
    date: rate.date,
    buy_rate: rate.buy_rate,
    sell_rate: rate.sell_rate,
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    put(`/exchange_rates/${rate.id}`)
  }

  return (
    <>
      <Head title="Edit Exchange Rate - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/exchange_rates">Exchange Rates</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/exchange_rates">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Edit Exchange Rate</h1>
              <p className="text-muted-foreground text-sm">
                {rate.currency_from.toUpperCase()}/{rate.currency_to.toUpperCase()} &middot; {rate.date}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input id="date" type="date" value={data.date} onChange={(e) => setData("date", e.target.value)} />
                  </div>

                  <Separator />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="buy_rate">Buy Rate</Label>
                      <Input
                        id="buy_rate"
                        type="number"
                        step="0.01"
                        value={data.buy_rate}
                        onChange={(e) => setData("buy_rate", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sell_rate">Sell Rate</Label>
                      <Input
                        id="sell_rate"
                        type="number"
                        step="0.01"
                        value={data.sell_rate}
                        onChange={(e) => setData("sell_rate", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href="/exchange_rates">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

ExchangeRateEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
